import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { ConfirmationDialog } from '@/lib/base/overlays';

import { disconnectActiveCampaign } from '../api/connect';

interface DisconnectConfirmationProps {
  orgId: number | string;
  isOpen: boolean;
  onClose: () => void;
  onDisconnected?: () => void;
}

const OUTCOME_CLASS =
  'flex gap-2 text-body-small text-[var(--color-text-secondary)]';

export function DisconnectConfirmation({
  orgId,
  isOpen,
  onClose,
  onDisconnected,
}: DisconnectConfirmationProps) {
  const queryClient = useQueryClient();

  const { mutate, isPending, isError, reset } = useMutation({
    mutationFn: () => disconnectActiveCampaign(orgId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['activeCampaignStatus', orgId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['activeCampaignSyncHealth', orgId],
      });
      onDisconnected?.();
      onClose();
    },
  });

  // Clear any error from a previous attempt each time the dialog opens so a
  // failed disconnect doesn't leave a stale error banner on the next session.
  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const handleConfirm = useCallback(() => {
    mutate();
  }, [mutate]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      variant='destructive'
      title='Disconnect ActiveCampaign?'
      description='Two-way sync with ActiveCampaign will stop right away. Nothing is deleted.'
      confirmLabel='Disconnect'
      isLoading={isPending}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      instance='disconnect-activecampaign'
    >
      <ul className='mt-3 space-y-2'>
        <li className={OUTCOME_CLASS}>
          <span aria-hidden='true'>•</span>
          <span>
            Contacts already synced stay in both Ahoy and ActiveCampaign.
          </span>
        </li>
        <li className={OUTCOME_CLASS}>
          <span aria-hidden='true'>•</span>
          <span>Reconnecting is quick.</span>
        </li>
      </ul>
      {isError && (
        <p
          role='alert'
          className='mt-3 text-body-small text-[var(--color-text-error-primary)]'
        >
          Couldn’t disconnect. Please try again.
        </p>
      )}
    </ConfirmationDialog>
  );
}
